import { useState, useRef, useEffect } from 'react';
import { ItemSuggestions } from './ItemSuggestions';
import './ItemInputWithSuggestions.css';

interface ItemInputWithSuggestionsProps {
  scenarioId: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value?: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  excludeItems?: string[];
  inputElementRef?: React.RefObject<HTMLInputElement | null>;
}

export function ItemInputWithSuggestions({
  scenarioId,
  value,
  onChange,
  onSubmit,
  placeholder = 'Add item...',
  autoFocus = false,
  excludeItems = [],
  inputElementRef,
}: ItemInputWithSuggestionsProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isNavigatingSuggestions, setIsNavigatingSuggestions] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const inputRef = inputElementRef ?? internalInputRef;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setIsNavigatingSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle mobile keyboard visibility - scroll input into view when keyboard resizes viewport
  useEffect(() => {
    if (!isFocused || !inputRef.current) return;

    const scrollInputIntoView = () => {
      if (inputRef.current && isFocused) {
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // Use visualViewport API if available (better mobile support)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', scrollInputIntoView);
      return () => {
        window.visualViewport?.removeEventListener('resize', scrollInputIntoView);
      };
    }
  }, [isFocused]);

  // Calculate dropdown position based on input location on screen
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;
      
      // Show above if there's more space above and below is tight (< 250px)
      // or if we're in the bottom third of the screen
      const shouldShowAbove = (spaceBelow < 250 && spaceAbove > spaceBelow) || 
                              (inputRect.top > viewportHeight * 0.66);
      
      setDropdownPosition(shouldShowAbove ? 'above' : 'below');
    }
  }, [showSuggestions]);

  const handleFocus = () => {
    setIsFocused(true);
    setShowSuggestions(true);
    
    // On mobile, scroll the input into view when keyboard opens
    // Use a timeout to wait for the virtual keyboard to appear
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Delay hiding to allow click on suggestion
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        setIsNavigatingSuggestions(false);
      }
    }, 200);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setShowSuggestions(true);
    setIsNavigatingSuggestions(false); // Reset navigation when typing
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Track arrow key usage for suggestion navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (showSuggestions && value.length > 0) {
        setIsNavigatingSuggestions(true);
        return; // Let ItemSuggestions handle arrow keys
      }
    }
    
    // Only let ItemSuggestions handle Enter if user is actively navigating suggestions
    if (e.key === 'Enter' && !e.shiftKey) {
      if (isNavigatingSuggestions) {
        // User has navigated with arrows, let ItemSuggestions handle Enter to select
        return;
      } else {
        // User just typed and pressed Enter, submit what they typed
        e.preventDefault();
        onSubmit();
        setShowSuggestions(false);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setIsNavigatingSuggestions(false);
      inputRef.current?.blur();
    }
  };

  const handleSelectSuggestion = (itemName: string) => {
    onChange(itemName);
    setShowSuggestions(false);
    setIsNavigatingSuggestions(false);
    inputRef.current?.focus();
    // Auto-submit when selecting from suggestions, passing the value directly
    onSubmit(itemName);
  };

  return (
    <div ref={containerRef} className={`item-input-container ${isFocused ? 'focused' : ''}`}>
      <input
        ref={inputRef}
        type="text"
        className="item-input"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete="off"
      />
      <ItemSuggestions
        scenarioId={scenarioId}
        searchQuery={value}
        onSelect={handleSelectSuggestion}
        isVisible={showSuggestions && value.length > 0}
        excludeItems={excludeItems}
        showSelectedIndex={isNavigatingSuggestions}
        position={dropdownPosition}
      />
      <div className="input-hint">
        {value.length > 0 ? (
          <>
            Press <kbd>Enter</kbd> to add, <kbd>↑↓</kbd> to navigate suggestions
          </>
        ) : (
          <span style={{visibility: 'hidden'}}>&nbsp;</span>
        )}
      </div>
    </div>
  );
}
